import {Brick, registerBrick} from 'olympe';
import {getCostCenterLambda, getSupplierAddressLambda, getWBSLambda, getTaxCodeFromMaterialLambda} from '../functionSignatures';

// Utility function to remove empty values from an object
// eslint-disable-next-line require-jsdoc
function removeEmpty(obj) {
    if (Array.isArray(obj)) {
        return obj
            .map((item) => removeEmpty(item))
            .filter(
                (item) =>
                    item !== '' &&
                    !(typeof item === 'object' && !Array.isArray(item) && Object.keys(item).length === 0),
            );
    } else if (obj !== null && typeof obj === 'object') {
        const newObj = {};
        for (const [key, value] of Object.entries(obj)) {
            const cleanedValue = removeEmpty(value);
            if (cleanedValue === '') continue;
            if (
                typeof cleanedValue === 'object' &&
                !Array.isArray(cleanedValue) &&
                Object.keys(cleanedValue).length === 0
            ) {
                continue;
            }
            newObj[key] = cleanedValue;
        }
        return newObj;
    }
    return obj;
}

// eslint-disable-next-line require-jsdoc
export default class CreateVertexRequestBodyBrick extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {string} pRJSON
     * @param {*} getSupplierAddress
     * @param {*} getCostCenter
     * @param {*} getWBS
     * @param {*} getTaxCodeFromMaterial
     * @param {function()} forwardEvent
     * @param {function(string)} setBody
     */
    async update($, [pRJSON, getSupplierAddress, getCostCenter, getWBS, getTaxCodeFromMaterial], [forwardEvent, setBody]) {
        // Parse the incoming PR JSON
        pRJSON = pRJSON.replace(/\n/g, '\\n');
        const pr = JSON.parse(pRJSON);

        const getCostCenterCategory = async (costCenter, companyCode) => {
            try {
                const response = await getCostCenterLambda($, getCostCenter, costCenter, companyCode);
                return JSON.parse(response);
            } catch (e) {
                console.error('Something went wrong', e);
                return [];
            }
        };

        const getSupplierAddressResult = async (vendorCode, requesterId) => {
            try {
                const response = await getSupplierAddressLambda($, getSupplierAddress, vendorCode, requesterId);

                if (!response) {
                    return [];
                }

                if (typeof response === 'string') {
                    if (response.trim() === '') {
                        return [];
                    }
                    const parsed = JSON.parse(response);
                    return parsed;
                }

                return response;
            } catch (e) {
                console.error('[SupplierAddress] Error fetching supplier address:', e);
                return [];
            }
        };


        const getWBSResult = async (userID, wbs, companyCode) => {
            try {
                const response = await getWBSLambda($, getWBS, userID, wbs, companyCode);
                return JSON.parse(response);
            } catch (e) {
                console.error('Error fetching WBS', e);
                return [];
            }
        };

        const getTaxCodeFromMaterialResult = async (materialNumber, plant) => {
            try {
                const response = await getTaxCodeFromMaterialLambda($, getTaxCodeFromMaterial, materialNumber, plant);
                return JSON.parse(response);
            } catch (e) {
                console.error('Error fetching supplier address', e);
                return [];
            }
        };

        // Account information
        let costCenter = '';
        let generalLedgerAccount = '';
        let projectNumber = '';
        let accountType = '';
        if (pr.financialInfo && Array.isArray(pr.financialInfo.accounts) && pr.financialInfo.accounts.length > 0) {
            const account = pr.financialInfo.accounts[0];
            costCenter = (account.costCenter && account.costCenter.value) || '';
            generalLedgerAccount = (account.glNumber && account.glNumber.value) || '';
            projectNumber = (account.wbs && account.wbs.value) || '';
            accountType = (account.accountType && account.accountType.value) || '';

            if (costCenter) {
                costCenter = costCenter.toString().padStart(10, '0');
            }
        }

        const companyCode = (pr.headerDetails && pr.headerDetails.companyCode && pr.headerDetails.companyCode.value) || '';

        const locationCode = pr.headerDetails.defaultPlant.value || '';

        // Convert the PR creation date to the documentDate (YYYY-MM-DD)
        const creationDate = pr.creationDate ? new Date(pr.creationDate) : null;
        const documentDate = creationDate ? creationDate.toISOString().slice(0, 10) : '';

        const deliveryTerm = pr.headerDetails.incoterms.data.value;

        // Map the delivery address to the buyer's destination.
        const defaultDeliveryAddress = (pr.headerDetails && pr.headerDetails.defaultDeliveryAddress) || {};

        const streetAddress1 =
            defaultDeliveryAddress.houseNumber && defaultDeliveryAddress.street ?
                `${defaultDeliveryAddress.houseNumber} ${defaultDeliveryAddress.street}` :
                defaultDeliveryAddress.street || '';

        const buyerDestination = {
            streetAddress1: streetAddress1,
            city: defaultDeliveryAddress.city || '',
            mainDivision: defaultDeliveryAddress.state || '',
            postalCode: defaultDeliveryAddress.postalCode || '',
            country: defaultDeliveryAddress.country || '',
        };

        // Build physicalOrigin from the supplier address.
        const supplierAddressResponse = await getSupplierAddressResult(pr.vendorCode, pr.requester.value);

        const supplierAddressData =
            Array.isArray(supplierAddressResponse) && supplierAddressResponse.length > 0 && supplierAddressResponse[0] !== null ?
                supplierAddressResponse[0] :
                {};

        const vendorPhysicalOriginData = {
            streetAddress1:
                supplierAddressData.house_num1 && supplierAddressData.street ?
                    `${supplierAddressData.house_num1} ${supplierAddressData.street}` :
                    supplierAddressData.street || '',
            city: supplierAddressData.city1 || '',
            mainDivision: supplierAddressData.county || '',
            postalCode: supplierAddressData.post_code1 || '',
            country: supplierAddressData.country || '',
        };

        // Check if an ordering address exists and is different from the vendor code.
        // If so, fetch it and use that data for administrativeOrigin.
        let administrativeOriginData = vendorPhysicalOriginData;
        const orderingAddressValue = pr.headerDetails.orderingAddress && pr.headerDetails.orderingAddress.value;
        if (orderingAddressValue && orderingAddressValue !== pr.vendorCode) {
            const orderingAddressResponse = await getSupplierAddressResult(orderingAddressValue, pr.requester.value);
            const orderingAddressData =
                Array.isArray(orderingAddressResponse) &&
                orderingAddressResponse.length > 0 &&
                orderingAddressResponse[0] !== null ?
                    orderingAddressResponse[0] :
                    {};
            if (Object.keys(orderingAddressData).length > 0) {
                administrativeOriginData = {
                    streetAddress1:
                        orderingAddressData.house_num1 && orderingAddressData.street ?
                            `${orderingAddressData.house_num1} ${orderingAddressData.street}` :
                            orderingAddressData.street || '',
                    city: orderingAddressData.city1 || '',
                    mainDivision: orderingAddressData.county || '',
                    postalCode: orderingAddressData.post_code1 || '',
                    country: orderingAddressData.country || '',
                };
            }
        }

        // Retrieve additional data: cost center category and WBS info.
        let globalCostCenterCategoryValue = '00';
        let globalWbsPOSID = '00';
        if (!pr.financialInfo.areAccountSplitByMaterial) {
            const costCenterCategoryResponse = await getCostCenterCategory(costCenter, companyCode);
            globalCostCenterCategoryValue =
                costCenterCategoryResponse && costCenterCategoryResponse.length > 0 ?
                    costCenterCategoryResponse[0].kosar || '00' :
                    '00';

            const wbsResponse = await getWBSResult(pr.requester.value, projectNumber, companyCode);
            globalWbsPOSID =
                wbsResponse && wbsResponse.length > 0 ?
                    (wbsResponse[0].GLAccLngtxt || '00') :
                    '00';
        }

        // Build line items from materials that have taxCode 'I1'
        const lineItems = [];
        let lineItemNumber = 1;
        for (const material of pr.materials || []) {
            if (material.taxCode && material.taxCode.value === 'I1') {
                // Use the current date in YYYYMMDD format
                const currentDate = new Date();
                const year = currentDate.getFullYear();
                const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
                const day = currentDate.getDate().toString().padStart(2, '0');
                const taxDate = Number(`${year}${month}${day}`);

                // Convert the price and quantity to numbers
                const quantityValue = Number(material.quantity) || 0;
                const extendedPrice = material.per && !material.freeItem ? Number(material.price) * Number(material.quantity) / material.per : 1.00;
                const unitOfMeasure = material.baseUnit || 'EA';

                const materialType = material.type && material.type.value || '';

                let materialNumber = material.name || '';
                const dashIndex = materialNumber.indexOf('-');
                if (dashIndex !== -1) {
                    materialNumber = materialNumber.substring(0, dashIndex).trim();
                }

                const taxCodeResponse = await getTaxCodeFromMaterialResult(materialNumber, material.plant.value);
                let taxCodeValue = '';
                if (Array.isArray(taxCodeResponse) && taxCodeResponse.length > 0) {
                    taxCodeValue = taxCodeResponse[0].glb_txcode;
                }

                let effectiveAccountType = accountType;
                let materialCostCenter = costCenter;
                let materialGeneralLedgerAccount = generalLedgerAccount;
                let materialProjectNumber = projectNumber;
                let materialCostCenterCategoryValue = globalCostCenterCategoryValue;
                let materialWbsPOSID = globalWbsPOSID;

                if (pr.financialInfo.areAccountSplitByMaterial && pr.financialInfo.accountDetailsByMaterial) {
                    const detail = pr.financialInfo.accountDetailsByMaterial.find(
                        (d) => d.material_id === material.id,
                    );
                    if (detail && detail.accounts && detail.accounts.length > 0) {
                        const splitAccount = detail.accounts[0];
                        effectiveAccountType = (splitAccount.accountType && splitAccount.accountType.value) || effectiveAccountType;
                        materialCostCenter = splitAccount.costCenter?.value || '';
                        materialGeneralLedgerAccount = splitAccount.glNumber?.value || '';
                        materialProjectNumber = splitAccount.wbs?.value || '';
                        if (materialCostCenter) {
                            materialCostCenter = materialCostCenter.toString().padStart(10, '0');
                        }
                        // Lookup cost center category and WBS for this material's account
                        const costCenterCategoryResponse = await getCostCenterCategory(materialCostCenter, companyCode);
                        materialCostCenterCategoryValue =
                            costCenterCategoryResponse && costCenterCategoryResponse.length > 0 ?
                                costCenterCategoryResponse[0].kosar || '00' :
                                '00';

                        const wbsResponse = await getWBSResult(pr.requester.value, materialProjectNumber, companyCode);
                        materialWbsPOSID =
                            wbsResponse && wbsResponse.length > 0 ?
                                (wbsResponse[0].GLAccLngtxt || '00') :
                                '00';
                    }
                }

                const flexibleCodeFields = [
                    {
                        fieldId: 1,
                        value: (() => {
                            let desc = material.name || ' ';
                            const dashIdx = desc.indexOf('-');
                            if (dashIdx !== -1) {
                                desc = desc.substring(dashIdx + 1).trim();
                            }
                            return desc;
                        })(),
                    },
                ];

                if (materialCostCenter && materialGeneralLedgerAccount) {
                    flexibleCodeFields.push({fieldId: 11, value: materialCostCenterCategoryValue});
                }

                if (materialType && materialType.trim() !== '') {
                    flexibleCodeFields.push({fieldId: 12, value: materialType});
                }

                if (materialProjectNumber) {
                    flexibleCodeFields.push({fieldId: 13, value: materialWbsPOSID});
                }

                if (effectiveAccountType === '2') {
                    const flexFields = [
                        {
                            fieldId: 1,
                            value: (() => {
                                let desc = material.name || ' ';
                                const dashIdx = desc.indexOf('-');
                                if (dashIdx !== -1) {
                                    desc = desc.substring(dashIdx + 1).trim();
                                }
                                return desc;
                            })(),
                        },
                    ];

                    const lineItem = {
                        lineItemNumber: lineItemNumber,
                        taxDate: taxDate,
                        extendedPrice: extendedPrice,
                        purchase: {value: taxCodeValue},
                        quantity: {unitOfMeasure: unitOfMeasure, value: quantityValue},
                        flexibleFields: {
                            flexibleCodeFields: flexFields,
                        },
                    };
                    lineItems.push(lineItem);
                    lineItemNumber++;
                    continue;
                }

                // Create a line item based on the mapping rules
                const lineItem = {
                    lineItemNumber: lineItemNumber,
                    taxDate: taxDate,
                    costCenter: materialCostCenter,
                    generalLedgerAccount: materialGeneralLedgerAccount,
                    projectNumber: materialProjectNumber,
                    extendedPrice: extendedPrice,
                    purchase: {value: taxCodeValue},
                    quantity: {unitOfMeasure: unitOfMeasure, value: quantityValue},
                    flexibleFields: {
                        flexibleCodeFields: flexibleCodeFields,
                    },
                };
                lineItems.push(lineItem);
                lineItemNumber++;
            }
        }

        // Build the final request body following the required structure
        let body = {
            purchaseMessageType: 'PURCHASE_ORDER',
            documentNumber: 'PURCHREQUEST1',
            orderType: 'easyreq',
            locationCode: locationCode,
            documentDate: documentDate,
            transactionType: 'PURCHASE',
            deliveryTerm: deliveryTerm,
            buyer: {
                company: 'Nestle',
                division: companyCode,
                destination: buyerDestination,
            },
            vendor: {
                vendorCode: {value: pr.vendorCode},
                administrativeOrigin: administrativeOriginData,
                physicalOrigin: vendorPhysicalOriginData,
            },
            lineItems: lineItems,
        };

        body = removeEmpty(body);
        setBody(JSON.stringify(body));
        forwardEvent();
    }
}

registerBrick('0194ffb3d730e20d5f7b', CreateVertexRequestBodyBrick);
